import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { InstUISettingsProvider, View } from '@smarterservices/smarter-ui';
import './ElementsLayout.css';
import SmarterElements, { decodeJWT, setupUnifiedContentResize } from '@smarterservices/smarter-elements';
/**
 * Layout component for Elements
 * This component handles iframe-specific functionality like:
 * - Auto-resizing
 * - Passing props from URL parameters
 * - Setting up message communication with parent
 */
const ElementsLayout = () => {
  const [searchParams] = useSearchParams();

  const token = searchParams.get('data');
  const decodedParams = decodeJWT(token);
  const debugMode = searchParams.get('debug') === 'true';

  // Determine if we're in modal mode
  const isModal = searchParams.get('modal') === 'true';
  const [contentHeight, setContentHeight] = useState('auto');
  const [initialHeight, setInitialHeight] = useState(decodedParams?.['_initialHeight'] || 500); // Default to 500px if not provided
  const containerRef = useRef(null);

  // Debug logging
  if (debugMode) {
    console.log('ElementsLayout searchParams:', {
      searchParams: Object.fromEntries(searchParams),
      searchParamsKeys: Array.from(searchParams.keys()),
      currentURL: window.location.href,
      currentSearch: window.location.search
    });
  }

  const environment = decodedParams?.['_environment'] || 'development';

  // remove internal parameters so they are not passed down to the specific element component...
  if (decodedParams) {
    delete decodedParams['_environment'];
    delete decodedParams['_initialHeight'];
  }

  // Effect to handle content height updates
  useEffect(() => {
    if (isModal && containerRef.current) {
      const updateHeight = () => {
        const height = containerRef.current.scrollHeight;
        setContentHeight(`${height}px`);
        // Notify parent about height changes
        window.parent.postMessage({ type: 'height', height }, '*');
      };

      // Initial height update
      updateHeight();

      // Update height on window resize
      window.addEventListener('resize', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
      };
    }
  }, [isModal]);

  // Generic resize system for all iframe content
  useEffect(() => {
    if (typeof window !== 'undefined' && window.parent && containerRef.current) {
      const sendResize = () => {
        requestAnimationFrame(() => {
          const container = containerRef.current;
          if (!container) return;

          // Calculate actual content height using multiple methods
          const containerHeight = container.scrollHeight;
          const bodyHeight = document.body.scrollHeight;
          const docHeight = document.documentElement.scrollHeight;

          const actualHeight = Math.max(containerHeight, bodyHeight, docHeight);

          if (debugMode) {
            console.log('ElementsLayout resize calculation:', {
              containerHeight,
              bodyHeight,
              docHeight,
              actualHeight,
              elementId: decodedParams?.elementId
            });
          }

          window.parent.postMessage({
            type: 'resize',
            height: actualHeight,
            width: container.offsetWidth,
            elementId: decodedParams?.elementId
          }, '*');
        });
      };

      // Initial resize with multiple attempts to catch content loading
      setTimeout(sendResize, 100);  // First quick attempt
      setTimeout(sendResize, 300);  // Second attempt
      setTimeout(sendResize, 600);  // Third attempt
      setTimeout(sendResize, 1000); // Fourth attempt for complex content like BlockKit

      // Extra attempts for BlockKit content which can be slow to render
      if (window.location.pathname.includes('blockkit')) {
        setTimeout(sendResize, 1500); // Extra delay for BlockKit
        setTimeout(sendResize, 2000); // Final attempt for BlockKit
      }

      // Listen for recalculate requests from parent
      const handleMessage = (event) => {
        if (event.data.type === 'recalculate-size') {
          console.log('Received recalculate-size request');
          setTimeout(sendResize, 200); // Increased delay to let layout settle
        }
      };

      window.addEventListener('message', handleMessage);

      // Set up ResizeObserver for dynamic content changes
      if (window.ResizeObserver) {
        let resizeTimeout;

        const observer = new ResizeObserver(() => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(sendResize, 200); // Allow content to fully render
        });

        // Observe the main container
        observer.observe(containerRef.current);
        observer.observe(document.body);

        return () => {
          window.removeEventListener('message', handleMessage);
          observer.disconnect();
          clearTimeout(resizeTimeout);
        };
      }

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [decodedParams?.elementId]);

  return (
    <InstUISettingsProvider>
      <div ref={containerRef} className="element-container" data-observe-resize>
        <Outlet context={{ environment, initialHeight, params: decodedParams || {}, isModal }} />
      </div>
    </InstUISettingsProvider>
  );
};

export default ElementsLayout;
